import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, AlertTitle, Box, Button, CircularProgress, Paper, Snackbar, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { hashKey, useQuery, useQueryClient } from '@tanstack/react-query';
import DotWaveLoader from '../../components/commonComponents/DotWaveLoader';
import KitChip from '../../components/ui/Chip';
import KitTooltip from '../../components/ui/Tooltip';
import { performancePanelSx, performanceToggleButtonSx, performanceToggleGroupSx } from '../../styles/performanceMsiStyles';
import { sectionTitleSx } from '../../styles/typography';
import { isHistoryContextConflict } from '../currentAttribution';
import { preparePerformanceQuery, useFrozenPerformanceValue, usePerformancePreparationRegistration, performancePreparationStamp } from '../publicationPreparation';
import { constantSourceRequest } from '../historicalPerformanceBasis';
import EpisodeDetail from './EpisodeDetail';
import EventEditor from './EventEditor';
import EventsPanel from './EventsPanel';
import {
    createExploreEvent, deleteExploreEvent, fetchExploreDetail, fetchExploreEnvironment, fetchExploreEvents, fetchExploreGrid,
    fetchExploreMarkets, fetchMarketCalendar, fetchStressLibrary, updateExploreEvent,
} from './exploreApi';
import {
    EVENT_NOTES_MAX, EVENT_TITLE_MAX, MAX_TABLE_COLUMNS, addColumns, buildGridRequest, cellOf, columnCatalogue, defaultColumnState, defaultSourceKeys,
    displayColumns, eventColumnId, isFoundColumnId, liveColumnIds, removeColumns, requestPeriods, sourceFromKey, sourceOptions,
} from './exploreColumns';
import { contextNote, matchedRunUp, monthSpanPeriod } from './exploreEnvironment';
import { formatDay, reasonsText } from './exploreFormat';
import HistoryStrip from './HistoryStrip';
import PortfolioChipPicker from './PortfolioChipPicker';
import StressGrid, { StressLegend } from './StressGrid';

// The selected factor dataset kept no factor-return history to replay.
const HISTORY_GAP_CODES = new Set(['factor_return_history_not_retained', 'factor_return_history_empty']);
// Both scans read the baseline, so they are unavailable together, and one
// sentence says so; the baseline's row says why.
const SCAN_UNAVAILABLE = new Set(['explore_worst_periods_unavailable', 'explore_best_periods_unavailable']);
const SCAN_UNAVAILABLE_TEXT = 'The baseline can’t be replayed, so its worst and best periods can’t be found.';
const QUERY_POLICY = Object.freeze({ retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false });
const NO_EPISODES = Object.freeze([]);
const NO_EVENTS = Object.freeze([]);
const NO_MARKETS = Object.freeze([]);
const VENDORS = Object.freeze({ barra: 'Barra', axioma: 'Axioma' });
const HORIZONS = Object.freeze({ LH: 'long horizon', MH: 'medium horizon', SH: 'short horizon' });
const panelSx = { p: { xs: 1, sm: 1.5 }, display: 'grid', gap: 1.25, minWidth: 0, ...performancePanelSx };
const titleSx = { ...sectionTitleSx, m: 0 };

// "Barra GEMLTL100 · history 30 Dec 1994 – 26 May 2026".
function modelLabel(grid) {
    const history = grid?.history;
    const context = grid?.factor_context;
    if (!history && !context) return null;
    const vendor = history?.vendor || context?.provider_vendor || '';
    const name = [VENDORS[vendor.toLowerCase()] || vendor, history?.model || context?.factor_model].filter(Boolean).join(' ');
    const horizon = HORIZONS[String(history?.horizon || context?.factor_horizon || '').toUpperCase()];
    const span = history?.first_return_date && history?.last_return_date
        ? `history ${formatDay(history.first_return_date)} – ${formatDay(history.last_return_date)}` : null;
    return [name, horizon, span].filter(Boolean).join(' · ');
}

// "31 years of factor history" from the first return date to the reading's date.
function historySpan(environment) {
    const first = environment?.history?.first_return_date;
    if (!first || !environment.as_of) return null;
    const years = Math.floor((Date.parse(environment.as_of) - Date.parse(first)) / (365.25 * 86_400_000));
    return years >= 1 ? `${years} year${years === 1 ? '' : 's'} of factor history` : null;
}

// Why events that did not fit stayed on the bench.
function didNotFit(skipped, catalogue) {
    if (!skipped.length) return null;
    if (skipped.length === 1) {
        const name = catalogue.byId.get(skipped[0])?.name || 'that event';
        return `The table is full: it holds ${MAX_TABLE_COLUMNS} events. Remove one to add “${name}”.`;
    }
    return `${skipped.length} events didn’t fit: the table holds ${MAX_TABLE_COLUMNS}. They stay on the bench.`;
}

/**
 * Compare & Explore: several portfolios replayed through the same history.
 * Portfolios are rows of the stress grid (the first is the baseline) and
 * events are its columns. The events panel above the grid holds them all:
 * the published standard library, your own named events, and the
 * baseline's worst and best periods found by the scans. A chip puts its
 * event in the table or back on the bench; the table holds one grid
 * request of 40. A cell opens the episode detail. Between them, the History section places
 * today in the factor history: a timeline of two linked panels, the market's
 * resemblance to today (outlining the closest past stress pattern) and each
 * portfolio's replay, shaded with the table's events. A click on a band
 * selects its event; a drag across months makes a new event.
 * Markets (ETFs such as S&P 500 (SPY)) can sit beside the portfolios as rows
 * tagged Actual, with a dashed line on the timeline: the market's actual
 * return over the same dates, never a replay and never the baseline.
 * The table's arrangement is the user's saved preference (`savedColumns`,
 * reported through `onSavedColumnsChange`; kept here when no owner is given).
 */
export default function CompareExploreTab({
    active = true,
    userUid = null,
    contextKey,
    contextReady = false,
    contextPending = false,
    listOfWhatIfPortfolios = null,
    savedColumns = null,
    onSavedColumnsChange = null,
    publicationPreparing = false,
    onRegisterPreparation = null,
    publicationSeed = null,
}) {
    const queryClient = useQueryClient();
    const options = useMemo(() => sourceOptions(listOfWhatIfPortfolios || []), [listOfWhatIfPortfolios]);
    const optionByKey = useMemo(() => new Map(options.map((option) => [option.key, option])), [options]);
    const labelFor = useCallback((key) => optionByKey.get(key)?.label || 'Unavailable portfolio', [optionByKey]);

    const [picked, setPicked] = useState(null);
    const sourceKeys = picked?.userUid === userUid ? picked.keys : defaultSourceKeys(options);
    const sources = sourceKeys.map(sourceFromKey);
    // A save, an edit or a Position Sizing change re-reads that portfolio.
    const revisions = sourceKeys.map((key) => [key, optionByKey.get(key)?.revision ?? null]);

    // The markets on offer: the same for every user, refreshed daily by Yahoo.
    const marketsQueryRead = useQuery({
        queryKey: ['explore_markets'],
        queryFn: ({ signal }) => fetchExploreMarkets({ signal }),
        enabled: active && !publicationPreparing,
        staleTime: 600_000,
        gcTime: 1_800_000,
        ...QUERY_POLICY,
    });
    const marketsQuery = useFrozenPerformanceValue(marketsQueryRead, publicationPreparing, contextKey);
    const marketCatalog = marketsQuery.data?.markets || NO_MARKETS;
    const [pickedMarkets, setPickedMarkets] = useState(null);
    const marketKeys = pickedMarkets?.userUid === userUid ? pickedMarkets.keys : NO_MARKETS;

    // The same for every user; an admin can hide episodes or publish a new version.
    const libraryQueryRead = useQuery({
        queryKey: ['stress_library'],
        queryFn: ({ signal }) => fetchStressLibrary({ signal }),
        enabled: active && !publicationPreparing,
        staleTime: 600_000,
        gcTime: 1_800_000,
        ...QUERY_POLICY,
    });
    const libraryQuery = useFrozenPerformanceValue(libraryQueryRead, publicationPreparing, contextKey);
    const library = libraryQuery.data?.episodes || NO_EPISODES;
    // A failed library read still replays your events and the found periods.
    const librarySettled = libraryQuery.isSuccess || libraryQuery.isError;

    // Your own events, private to you.
    const eventsKey = ['compare_explore_events', userUid];
    const eventsQueryRead = useQuery({
        queryKey: eventsKey,
        queryFn: ({ signal }) => fetchExploreEvents({ signal }),
        enabled: active && !publicationPreparing,
        staleTime: 300_000,
        gcTime: 1_800_000,
        ...QUERY_POLICY,
    });
    const eventsQuery = useFrozenPerformanceValue(eventsQueryRead, publicationPreparing, contextKey);
    const events = eventsQuery.data?.events || NO_EVENTS;
    const maxEvents = eventsQuery.data?.maxEvents ?? null;
    const eventsSettled = eventsQuery.isSuccess || eventsQuery.isError;

    const controlled = typeof onSavedColumnsChange === 'function';
    const [localColumns, setLocalColumns] = useState(null);
    const defaultColumns = useMemo(() => defaultColumnState(library), [library]);
    const columnState = (controlled ? savedColumns : localColumns?.userUid === userUid && localColumns.state) || defaultColumns;
    const updateColumns = useCallback((next) => {
        if (controlled) onSavedColumnsChange(next);
        else setLocalColumns({ userUid, state: next });
    }, [controlled, onSavedColumnsChange, userUid]);

    // The table: saved columns less those known to be gone (an episode the
    // library dropped, an event deleted elsewhere). Found periods come from
    // the grid, so the request reads only the library and your events.
    const index = useMemo(() => columnCatalogue(library, events), [library, events]);
    const table = {
        ...columnState,
        columns: liveColumnIds(columnState.columns, index, { libraryLoaded: libraryQuery.isSuccess, eventsLoaded: eventsQuery.isSuccess }),
    };
    const inTable = new Set(table.columns);
    // Edits that finish after a request start from the table as it is then.
    const tableRef = useRef(table);
    tableRef.current = table;

    const [basis, setBasis] = useState('result');
    const [selection, setSelection] = useState(null);
    const [notice, setNotice] = useState(null);
    const [stripNote, setStripNote] = useState(null);
    const [editor, setEditor] = useState(null);
    const [stripSelection, setStripSelection] = useState(null);
    const editorCount = useRef(0);
    const stripAnchorRef = useRef(null);
    const newEventRef = useRef(null);
    const detailRef = useRef(null);

    const periods = requestPeriods(index, table.columns);
    const gridKey = ['performance_explore_grid', contextKey, libraryQuery.data?.version ?? null, revisions,
        hashKey([periods.map(({ start_date: start, end_date: end }) => [start, end])]), marketKeys];
    const [dismissedPreparedUnavailable, setDismissedPreparedUnavailable] = useState(null);
    const preparedUnavailable = publicationSeed?.contextKey === contextKey && dismissedPreparedUnavailable !== contextKey
        ? publicationSeed.unavailableError : null;
    const preparedEnvironmentUnavailable = publicationSeed?.contextKey === contextKey && dismissedPreparedUnavailable !== contextKey
        ? publicationSeed.environmentError : null;
    const gridQueryRead = useQuery({
        queryKey: gridKey,
        queryFn: async ({ signal }) => {
            const response = await fetchExploreGrid(buildGridRequest(sources, periods, marketKeys), { signal });
            return { response, sourceKeys, marketKeys };
        },
        enabled: !preparedUnavailable && !publicationPreparing && active && contextReady && sources.length > 0 && librarySettled && eventsSettled,
        // The previous frame stays on screen while another comparison in the
        // same context loads; it never crosses a context change.
        placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === contextKey ? previous : undefined),
        staleTime: 300_000,
        gcTime: 600_000,
        ...QUERY_POLICY,
    });
    const gridQuery = useFrozenPerformanceValue(preparedUnavailable ? { ...gridQueryRead, error: preparedUnavailable, isPending: false, isFetching: false,
        refetch: (...args) => { setDismissedPreparedUnavailable(contextKey); return gridQueryRead.refetch(...args); } } : gridQueryRead, publicationPreparing, contextKey);
    // The last replay that loaded in this context. When a later one fails, the
    // grid keeps showing it as it was and a snackbar says why.
    const lastLoaded = useRef(null);
    if (gridQuery.data && !gridQuery.isPlaceholderData) lastLoaded.current = { contextKey, frame: gridQuery.data };
    const refreshFailed = Boolean(contextReady && gridQuery.error) && !HISTORY_GAP_CODES.has(gridQuery.error.code)
        && lastLoaded.current?.contextKey === contextKey;
    const gridFailed = Boolean(gridQuery.error) && !refreshFailed;
    const [dismissedFailure, setDismissedFailure] = useState(null);
    const dismissFailure = () => setDismissedFailure(gridQuery.errorUpdatedAt);
    let frame = null;
    if (refreshFailed) frame = lastLoaded.current.frame;
    else if (contextReady && !gridQuery.error) frame = gridQuery.data;
    const grid = frame?.response || null;
    // Rows follow the portfolios as picked. One the shown frame doesn't hold
    // yet (just added) is pending: its cells shimmer until its replay arrives.
    const frameRows = new Map((frame?.sourceKeys || []).map((key, index) => [key, grid?.rows[index] || null]));
    const entries = (refreshFailed ? frame.sourceKeys : sourceKeys).map((key, index) => ({
        key, label: labelFor(key), baseline: index === 0,
        row: frameRows.get(key) || null, pending: Boolean(frame) && !frameRows.has(key),
    }));
    const baselineEntry = entries[0] || null;
    // Markets follow the portfolios as picked, pending until a frame has them.
    const frameMarkets = new Map((frame?.marketKeys || []).map((key, index) => [key, grid?.market_rows?.[index] || null]));
    const marketEntries = (refreshFailed ? frame.marketKeys || NO_MARKETS : marketKeys).map((key) => {
        const row = frameMarkets.get(key) || null;
        const label = marketCatalog.find((market) => market.key === key)?.label || (row ? `${row.label} (${key})` : key);
        return { key: `market:${key}`, market: key, label, row, pending: Boolean(frame) && !frameMarkets.has(key) };
    });
    // The found periods come from the shown frame's scan, so they keep that
    // frame's baseline until a new baseline's replay arrives.
    const foundLabel = labelFor((frame?.sourceKeys || sourceKeys)[0]);
    const catalogue = useMemo(() => columnCatalogue(library, events, grid, foundLabel), [library, events, grid, foundLabel]);
    const loadedIds = new Set((grid?.columns || []).map((column) => column.column_id));
    // A column just put in the table shimmers until the refreshed grid has
    // it; after a failed refresh, columns the last frame lacks are left out.
    const shownColumns = displayColumns(catalogue, table.columns, table.sort, baselineEntry?.row)
        .filter((column) => !(refreshFailed && !loadedIds.has(column.responseId)))
        .map((column) => (grid && !loadedIds.has(column.responseId) ? { ...column, pending: true } : column));
    const scanUnavailable = Boolean(grid?.reasons?.some((reason) => SCAN_UNAVAILABLE.has(reason?.reason_code)));
    const otherReasons = (grid?.reasons || []).filter((reason) => !SCAN_UNAVAILABLE.has(reason?.reason_code));
    const missingScan = Boolean(grid) && table.columns.some((id) => isFoundColumnId(id) && !catalogue.byId.has(id));

    const chosen = selection?.userUid === userUid ? selection : null;
    const selectedEntry = entries.find((entry) => entry.key === chosen?.sourceKey && entry.row?.available)
        || entries.find((entry) => entry.row?.available) || null;
    const selectedColumn = shownColumns.find((column) => column.id === chosen?.columnId) || shownColumns[0] || null;
    const selectedCell = selectedEntry && selectedColumn && !selectedColumn.pending ? cellOf(selectedEntry.row, selectedColumn) : null;
    const detailEnabled = Boolean(active && selectedCell?.available && selectedEntry.row.input_digest);
    const detailQueryRead = useQuery({
        queryKey: ['performance_explore_detail', contextKey, selectedEntry?.key, selectedEntry?.row?.input_digest,
            selectedColumn?.start_date, selectedColumn?.end_date],
        queryFn: ({ signal }) => {
            const source = constantSourceRequest(sourceFromKey(selectedEntry.key));
            return fetchExploreDetail({
                ...(source ? { constant_portfolio_source: source } : {}),
                expected_context_digest: selectedEntry.row.context_digest ?? null,
                expected_input_digest: selectedEntry.row.input_digest,
                start_date: selectedColumn.start_date,
                end_date: selectedColumn.end_date,
            }, { signal });
        },
        enabled: detailEnabled && !publicationPreparing,
        staleTime: 300_000,
        gcTime: 300_000,
        ...QUERY_POLICY,
    });
    const detailQuery = useFrozenPerformanceValue(detailQueryRead, publicationPreparing, contextKey);
    const calendarQueryRead = useQuery({
        queryKey: ['market_calendar', selectedColumn?.start_date, selectedColumn?.end_date],
        queryFn: ({ signal }) => fetchMarketCalendar(selectedColumn.start_date, selectedColumn.end_date, { signal }),
        // Only beside a loaded grid, where the episode detail shows it.
        enabled: Boolean(!publicationPreparing && active && grid && selectedColumn),
        staleTime: 600_000,
        gcTime: 600_000,
        ...QUERY_POLICY,
    });

    const calendarQuery = useFrozenPerformanceValue(calendarQueryRead, publicationPreparing, contextKey);
    // Today's environment comes from the factor release every row shares. The
    // baseline's row fences the read, or else the first other row whose source
    // was captured: a context digest is enough, even for a row that can't be replayed.
    const anchorEntry = entries.find((entry) => entry.row?.context_digest) || null;
    const environmentQueryRead = useQuery({
        queryKey: ['performance_explore_environment', contextKey, anchorEntry?.key ?? null, anchorEntry?.row?.context_digest ?? null,
            libraryQuery.data?.version ?? null],
        queryFn: ({ signal }) => {
            const source = constantSourceRequest(sourceFromKey(anchorEntry.key));
            return fetchExploreEnvironment({ portfolio: { ...(source ? { constant_portfolio_source: source } : {}),
                expected_context_digest: anchorEntry.row.context_digest } }, { signal });
        },
        enabled: Boolean(!preparedEnvironmentUnavailable && !publicationPreparing && active && contextReady && anchorEntry && librarySettled),
        // As the grid does: the previous reading stays up within one context.
        placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === contextKey ? previous : undefined),
        staleTime: 600_000,
        gcTime: 1_800_000,
        ...QUERY_POLICY,
    });

    const environmentQuery = useFrozenPerformanceValue(preparedEnvironmentUnavailable
        ? { ...environmentQueryRead, error: preparedEnvironmentUnavailable, isPending: false, isFetching: false,
            refetch: (...args) => { setDismissedPreparedUnavailable(contextKey); return environmentQueryRead.refetch(...args); } }
        : environmentQueryRead, publicationPreparing, contextKey);
    const say = (text) => setNotice(text ? { userUid, text } : null);
    const selectCell = useCallback((sourceKey, columnId) => {
        setSelection({ userUid, sourceKey, columnId });
        // Below three panels across, the detail is under the fold.
        if (window.matchMedia?.('(max-width: 1199.95px)')?.matches) {
            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)')?.matches;
            detailRef.current?.scrollIntoView?.({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
        }
    }, [userUid]);
    // A band on the timeline selects its event for the shown row, leaving the timeline in view.
    const selectEvent = (columnId) => setSelection({ userUid, sourceKey: selectedEntry?.key ?? sourceKeys[0], columnId });
    // Puts events in the table while it has room and says which didn't fit.
    const putInTable = (ids) => {
        const { state, skipped } = addColumns(tableRef.current, ids);
        if (state.columns.length !== tableRef.current.columns.length) updateColumns(state);
        say(didNotFit(skipped, catalogue));
    };
    const takeFromTable = (ids) => {
        updateColumns(removeColumns(tableRef.current, ids));
        say(null);
    };
    const toggleEvent = (id) => (inTable.has(id) ? takeFromTable([id]) : putInTable([id]));
    // The grid first: a replay whose inputs changed comes back with a new
    // digest, which keys a fresh detail. Then the details whose replay came
    // back unchanged, since their digest keeps a failed answer until it is
    // asked again; asking an old digest again would only fail. Refreshes can
    // overlap, so they are counted.
    const [refreshes, setRefreshes] = useState(0);
    const refreshGrid = async () => {
        setDismissedPreparedUnavailable(contextKey);
        setRefreshes((count) => count + 1);
        try {
            await queryClient.invalidateQueries({ queryKey: ['performance_explore_grid', contextKey] });
            const digests = new Set((queryClient.getQueryData(gridKey)?.response?.rows || []).map((row) => row?.input_digest).filter(Boolean));
            await queryClient.invalidateQueries({ queryKey: ['performance_explore_detail', contextKey], predicate: (query) => digests.has(query.queryKey[3]) });
        } finally {
            setRefreshes((count) => count - 1);
        }
    };
    // Making, editing and deleting your events. A new event can be made once
    // your events have loaded, while you have fewer than the most you can keep.
    let cannotCreate = null;
    if (!eventsQuery.isSuccess) cannotCreate = 'Your events haven’t loaded yet, so a new one can’t be saved.';
    else if (maxEvents !== null && events.length >= maxEvents) {
        cannotCreate = `You have ${maxEvents} events, the most you can keep. Delete one to add another.`;
    }
    const openEditor = (next) => {
        editorCount.current += 1;
        setEditor({ userUid, key: editorCount.current, eventId: null, ...next });
    };
    const closeEditor = () => {
        setEditor(null);
        setStripSelection(null);
    };
    const storeEvent = (event) => queryClient.setQueryData(eventsKey, (old) => old && {
        ...old, events: [...old.events.filter((item) => item.id !== event.id), event],
    });
    // An event deleted on another device leaves this one's chip and column.
    const forgetEvent = (eventId) => {
        queryClient.setQueryData(eventsKey, (old) => old && { ...old, events: old.events.filter((item) => item.id !== eventId) });
        const columnId = eventColumnId(eventId);
        if (tableRef.current.columns.includes(columnId)) updateColumns(removeColumns(tableRef.current, [columnId]));
    };
    const deletedElsewhere = (error) => error?.code === 'compare_explore_event_not_found';
    const saveEvent = async (draft, { addToTable }) => {
        let event;
        try {
            event = editor.mode === 'edit' ? await updateExploreEvent(editor.eventId, draft) : await createExploreEvent(draft);
        } catch (error) {
            if (editor.mode !== 'edit' || !deletedElsewhere(error)) throw error;
            forgetEvent(editor.eventId);
            closeEditor();
            say('That event was deleted elsewhere, so it has gone from your events.');
            return;
        }
        storeEvent(event);
        if (addToTable) putInTable([eventColumnId(event.id)]);
        else say(null);
        closeEditor();
    };
    const deleteEvent = async (column) => {
        try {
            await deleteExploreEvent(column.eventId);
        } catch (error) {
            if (!deletedElsewhere(error)) throw error;
        }
        // Its chip and info button are about to go; keep focus in the panel.
        newEventRef.current?.focus({ preventScroll: true });
        forgetEvent(column.eventId);
    };
    const onEventAction = (action, column, anchorEl) => {
        if (action === 'delete') return deleteEvent(column);
        if (action !== 'edit' && cannotCreate) {
            say(cannotCreate);
            return undefined;
        }
        const dates = { start_date: column.start_date, end_date: column.end_date };
        if (action === 'edit') {
            openEditor({ mode: 'edit', eventId: column.eventId, anchorEl, initial: { title: column.name, ...dates, notes: column.notes } });
        } else {
            // A copy keeps the standard event's description as its notes; a
            // found period's name already says whose scan found it.
            const notes = action === 'copy' ? column.description : '';
            openEditor({ mode: 'create', anchorEl, initial: { title: column.name.slice(0, EVENT_TITLE_MAX), ...dates, notes: notes.slice(0, EVENT_NOTES_MAX) } });
        }
        return undefined;
    };
    const newEvent = (anchorEl) => openEditor({ mode: 'create', anchorEl, initial: { title: '', start_date: null, end_date: null, notes: '' } });
    // Months dragged across on the strip become a new event's dates.
    const environment = contextReady ? environmentQuery.data || null : null;
    const selectMonths = (span) => {
        const period = monthSpanPeriod(span.from, span.to, environment?.history?.first_return_date, environment?.history?.last_return_date);
        const problem = period ? cannotCreate : 'Those months have no factor history to replay.';
        setStripNote(problem ? { userUid, text: problem } : null);
        if (problem) return;
        setStripSelection({ userUid, ...span });
        openEditor({ mode: 'create', anchorEl: () => stripAnchorRef.current, initial: { title: '', ...period, notes: '' } });
    };

    const preparationStamp = performancePreparationStamp([active, contextKey, sourceKeys, revisions,
        table, marketKeys, selection, libraryQuery.data?.version, eventsQuery.data]);
    usePerformancePreparationRegistration(onRegisterPreparation, async ({ nextProps, isCurrentRequest }) => {
        if (!active) throw new Error('Compare & Explore is no longer visible.');
        if (!nextProps.contextReady || sources.length === 0) return { workspace: 'compare', unavailable: true };
        // A first visit during publication has no accepted catalogs to read
        // from the frozen view. Prepare those same cache entries privately;
        // no old-context portfolio query or second mounted workspace is needed.
        const [preparedLibrary, preparedEvents, preparedMarkets] = await Promise.all([
            preparePerformanceQuery(queryClient, ['stress_library'],
                ({ signal }) => fetchStressLibrary({ signal }), isCurrentRequest, { staleTime: 600_000 }),
            preparePerformanceQuery(queryClient, eventsKey,
                ({ signal }) => fetchExploreEvents({ signal }), isCurrentRequest),
            preparePerformanceQuery(queryClient, ['explore_markets'],
                ({ signal }) => fetchExploreMarkets({ signal }), isCurrentRequest, { staleTime: 600_000 }),
        ]);
        const nextLibrary = preparedLibrary.episodes;
        const nextEvents = preparedEvents.events;
        const nextColumnState = (controlled ? savedColumns : localColumns?.userUid === userUid && localColumns.state)
            || defaultColumnState(nextLibrary);
        const nextIndex = columnCatalogue(nextLibrary, nextEvents);
        const nextTable = { ...nextColumnState,
            columns: liveColumnIds(nextColumnState.columns, nextIndex, { libraryLoaded: true, eventsLoaded: true }) };
        const nextPeriods = requestPeriods(nextIndex, nextTable.columns);
        const nextContext = nextProps.contextKey;
        const nextOptions = new Map(sourceOptions(nextProps.listOfWhatIfPortfolios || []).map(option => [option.key, option]));
        const nextSourceKeys = picked?.userUid === userUid ? picked.keys : defaultSourceKeys([...nextOptions.values()]);
        const nextSources = nextSourceKeys.map(sourceFromKey);
        const nextRevisions = nextSourceKeys.map(key => [key, nextOptions.get(key)?.revision ?? null]);
        const nextGridKey = ['performance_explore_grid', nextContext, preparedLibrary.version ?? null,
            nextRevisions, hashKey([nextPeriods.map(({ start_date: start, end_date: end }) => [start, end])]), marketKeys];
        let responseFrame;
        try {
            responseFrame = await preparePerformanceQuery(queryClient, nextGridKey,
                async ({ signal }) => ({ response: await fetchExploreGrid(buildGridRequest(nextSources, nextPeriods, marketKeys), { signal }), sourceKeys: nextSourceKeys, marketKeys }), isCurrentRequest, { staleTime: 0 });
        } catch (error) {
            if (!HISTORY_GAP_CODES.has(error.code)) throw error;
            return { workspace: 'compare', unavailable: true, seed: { contextKey: nextContext, unavailableError: error } };
        }
        const nextGrid = responseFrame.response;
        const nextEntries = nextSourceKeys.map((key, index) => ({ key, row: nextGrid.rows[index] }));
        const nextAnchor = nextEntries.find(entry => entry.row?.context_digest);
        const nextBaseline = nextEntries[0];
        const nextCatalogue = columnCatalogue(nextLibrary, nextEvents, nextGrid, nextOptions.get(nextSourceKeys[0])?.label || 'Unavailable portfolio');
        const nextColumns = displayColumns(nextCatalogue, nextTable.columns, nextTable.sort, nextBaseline?.row)
            .map(column => ({ ...column, pending: !(nextGrid.columns || []).some(item => item.column_id === column.responseId) }));
        const nextEntry = nextEntries.find(entry => entry.key === chosen?.sourceKey && entry.row?.available)
            || nextEntries.find(entry => entry.row?.available);
        const nextColumn = nextColumns.find(column => column.id === chosen?.columnId) || nextColumns[0];
        const nextCell = nextEntry && nextColumn && !nextColumn.pending ? cellOf(nextEntry.row, nextColumn) : null;
        const work = []; let environmentError = null;
        if (nextAnchor) {
            const source = constantSourceRequest(sourceFromKey(nextAnchor.key));
            work.push(preparePerformanceQuery(queryClient,
                ['performance_explore_environment', nextContext, nextAnchor.key, nextAnchor.row.context_digest, preparedLibrary.version ?? null],
                ({ signal }) => fetchExploreEnvironment({ portfolio: { ...(source ? { constant_portfolio_source: source } : {}),
                    expected_context_digest: nextAnchor.row.context_digest } }, { signal }), isCurrentRequest).catch(error => {
                    if (!HISTORY_GAP_CODES.has(error.code)) throw error;
                    environmentError = error;
                }));
        }
        if (nextColumn) work.push(preparePerformanceQuery(queryClient,
            ['market_calendar', nextColumn.start_date, nextColumn.end_date],
            ({ signal }) => fetchMarketCalendar(nextColumn.start_date, nextColumn.end_date, { signal }), isCurrentRequest,
            { staleTime: 600_000 }));
        if (nextCell?.available && nextEntry.row.input_digest) {
            const source = constantSourceRequest(sourceFromKey(nextEntry.key));
            work.push(preparePerformanceQuery(queryClient,
                ['performance_explore_detail', nextContext, nextEntry.key, nextEntry.row.input_digest, nextColumn.start_date, nextColumn.end_date],
                ({ signal }) => fetchExploreDetail({ ...(source ? { constant_portfolio_source: source } : {}),
                    expected_context_digest: nextEntry.row.context_digest ?? null, expected_input_digest: nextEntry.row.input_digest,
                    start_date: nextColumn.start_date, end_date: nextColumn.end_date }, { signal }), isCurrentRequest));
        }
        await Promise.all(work);
        const catalogStamp = performancePreparationStamp([preparedLibrary, preparedEvents, preparedMarkets]);
        return { workspace: 'compare', seed: environmentError ? { contextKey: nextContext, environmentError } : null, canCommit: () => catalogStamp === performancePreparationStamp([
            queryClient.getQueryData(['stress_library']), queryClient.getQueryData(eventsKey), queryClient.getQueryData(['explore_markets']),
        ]) };
    }, preparationStamp);
    if (!active) return null;

    const model = modelLabel(grid);
    const firstLoad = contextReady && !frame && !gridQuery.error;
    const noReplay = Boolean(grid) && !entries.some((entry) => entry.row?.available);
    const historyGap = HISTORY_GAP_CODES.has(gridQuery.error?.code) ? gridQuery.error : null;
    const messages = {};
    if (libraryQuery.isPending) messages.standard = 'Loading the stress library…';
    else if (libraryQuery.isError) messages.standard = 'The stress library couldn’t be loaded.';
    else if (!library.length) messages.standard = 'No standard-library events are published yet.';
    if (eventsQuery.isPending) messages.yours = 'Loading your events…';
    else if (eventsQuery.isError) messages.yours = 'Your events couldn’t be loaded.';
    else if (!events.length) messages.yours = 'None yet. Drag across the History timeline, or use New event, to name a period of your own.';
    else if (cannotCreate) messages.yours = cannotCreate;
    if (!grid) messages.found = 'The baseline’s worst and best periods appear once the grid has results.';
    else if (scanUnavailable) messages.found = SCAN_UNAVAILABLE_TEXT;
    else if (!catalogue.found.length) messages.found = 'No worst or best periods were found for the baseline.';

    const environmentError = environment ? null : environmentQuery.error;
    let historyBody;
    // After a failed refresh the grid keeps its last results, and so does this.
    if (!contextReady || gridFailed || firstLoad) {
        historyBody = (
            <Typography variant="body2" color="text.secondary">
                {historyGap ? 'There is no factor history to compare today with.' : 'The timeline appears once the grid has results.'}
            </Typography>
        );
    } else if (!anchorEntry) {
        historyBody = (
            <Typography variant="body2" color="text.secondary">
                None of these portfolios can be read, so today can’t be placed in the factor history.
            </Typography>
        );
    } else if (environmentError && HISTORY_GAP_CODES.has(environmentError.code)) {
        historyBody = (
            <Alert severity="info">
                <AlertTitle>No factor history to compare with</AlertTitle>
                {environmentError.message || 'The selected factor dataset kept no factor-return history.'}
            </Alert>
        );
    } else if (environmentError && isHistoryContextConflict(environmentError)) {
        // A new grid carries the new context digest, which reads the history again.
        historyBody = (
            <Alert severity="info" action={<Button size="small" onClick={refreshGrid}>Refresh</Button>}>
                The portfolios changed since the grid loaded. Refresh the grid to read the current factor history.
            </Alert>
        );
    } else if (environmentError) {
        historyBody = (
            <Alert severity="warning" action={<Button size="small" onClick={() => { void environmentQuery.refetch(); }}>Retry</Button>}>
                {environmentError.message || 'The timeline couldn’t be loaded.'}
            </Alert>
        );
    } else if (!environment) {
        historyBody = <DotWaveLoader height={200} label="Loading the timeline" />;
    } else {
        // Each portfolio's whole-history replay; one being added or that
        // can't be replayed has no line yet.
        const timelineRows = entries.map((entry) => {
            let status;
            if (entry.pending) status = 'loading';
            else if (entry.row && !entry.row.available) status = 'unavailable';
            return { key: entry.key, label: entry.label, baseline: entry.baseline, status, line: entry.row?.available ? entry.row.monthly_replay || null : null };
        });
        const timelineMarkets = marketEntries.map((entry) => {
            let status;
            if (entry.pending) status = 'loading';
            else if (entry.row && !entry.row.available) status = 'unavailable';
            return { key: entry.key, market: entry.market, label: entry.label, status, line: entry.row?.available ? entry.row.monthly_drawdown || null : null };
        });
        historyBody = (
            <>
                <HistoryStrip months={environment.months} rows={timelineRows} markets={timelineMarkets} events={shownColumns}
                    selectedEventId={selectedColumn?.id ?? null} onSelectEvent={selectEvent} runUp={matchedRunUp(environment.resemblance)}
                    contextNote={contextNote(environment.methodology)} baselineLabel={foundLabel}
                    selection={stripSelection?.userUid === userUid ? stripSelection : null} onSelect={selectMonths} anchorRef={stripAnchorRef} />
                {stripNote?.userUid === userUid && <Alert severity="info" onClose={() => setStripNote(null)}>{stripNote.text}</Alert>}
            </>
        );
    }

    let gridBody;
    if (!contextReady && !contextPending) {
        gridBody = <Alert severity="info">Compare &amp; Explore needs a published Watchlist and a factor dataset. Once both are ready, your portfolios appear here.</Alert>;
    } else if (historyGap) {
        gridBody = (
            <Alert severity="info">
                <AlertTitle>No factor history to replay</AlertTitle>
                {historyGap.message || 'The selected factor dataset kept no factor-return history.'} Events are replayed from the dataset’s retained history, so choose a factor dataset that kept it.
            </Alert>
        );
    } else if (gridFailed) {
        gridBody = (
            <Alert severity={isHistoryContextConflict(gridQuery.error) ? 'info' : 'warning'} action={<Button size="small" onClick={() => { void gridQuery.refetch(); }}>Retry</Button>}>
                {isHistoryContextConflict(gridQuery.error) ? 'The portfolios changed while the grid loaded. Retry to replay the current holdings.'
                    : gridQuery.error.message || 'The stress grid couldn’t be calculated.'}
            </Alert>
        );
    } else if (!shownColumns.length && !(firstLoad || !contextReady)) {
        gridBody = (
            <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, px: 2, py: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    No events in the table. Pick some above, or drag across the History timeline to make one.
                </Typography>
            </Box>
        );
    } else {
        gridBody = (
            <StressGrid entries={entries} markets={marketEntries} columns={shownColumns} basis={basis} baselineLabel={foundLabel}
                loading={firstLoad || !contextReady}
                selected={selectedEntry && selectedColumn ? { sourceKey: selectedEntry.key, columnId: selectedColumn.id } : null}
                onSelectCell={selectCell} onRemoveColumn={(id) => takeFromTable([id])} onEventAction={onEventAction} />
        );
    }

    return (
        <Box sx={{ display: 'grid', gap: 1.5, minWidth: 0 }}>
            <Paper sx={{ ...panelSx, gap: 1 }}>
                <Box role="group" aria-label="Compare and explore controls" sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                    <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>Compare &amp; Explore</Typography>
                    <PortfolioChipPicker options={options} value={sourceKeys} onChange={(keys) => setPicked({ userUid, keys })}
                        marketOptions={marketCatalog} markets={marketKeys} onMarketsChange={(keys) => setPickedMarkets({ userUid, keys })} />
                    {model && (
                        <KitTooltip content="The factor model and history every cell is replayed with">
                            <KitChip tabIndex={0} label={model} className="!normal-case !font-medium" />
                        </KitTooltip>
                    )}
                    <Box sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                        <Box role={gridQuery.isFetching ? 'status' : undefined} aria-label={gridQuery.isFetching ? 'Updating the stress grid' : undefined}
                            sx={{ width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            {gridQuery.isFetching && <CircularProgress size={14} />}
                        </Box>
                        <ToggleButtonGroup exclusive size="small" value={basis} aria-label="Show results as" sx={performanceToggleGroupSx}
                            onChange={(_, value) => { if (value) setBasis(value); }}>
                            <ToggleButton value="result" sx={performanceToggleButtonSx}>Result</ToggleButton>
                            <ToggleButton value="gap" sx={performanceToggleButtonSx}>vs baseline</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>
            </Paper>

            <Paper component="section" aria-labelledby="compare-explore-events-title" sx={panelSx}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                    <Typography id="compare-explore-events-title" component="h3" sx={{ ...titleSx, mr: 'auto' }}>Events</Typography>
                    <Typography variant="caption" color="text.secondary">{table.columns.length} of {MAX_TABLE_COLUMNS} in the table</Typography>
                </Box>
                {notice?.userUid === userUid && <Alert severity="info" onClose={() => say(null)}>{notice.text}</Alert>}
                {eventsQuery.isError && (
                    <Alert severity="warning" action={<Button size="small" onClick={() => { void eventsQuery.refetch(); }}>Retry</Button>}>
                        Your events couldn’t be loaded, so they aren’t in the grid. The standard library and the worst and best periods still replay.
                    </Alert>
                )}
                <EventsPanel catalogue={catalogue} inTable={inTable} baselineLabel={foundLabel} messages={messages} canCreate={!cannotCreate}
                    newEventRef={newEventRef}
                    onToggle={toggleEvent} onAddAll={putInTable} onRemoveAll={takeFromTable} onNewEvent={newEvent} onEventAction={onEventAction} />
            </Paper>

            <Paper component="section" aria-labelledby="compare-explore-grid-title" sx={panelSx}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Typography id="compare-explore-grid-title" component="h3" sx={{ ...titleSx, mr: 'auto' }}>
                        Stress grid · factor replay of today’s holdings
                    </Typography>
                    <ToggleButtonGroup exclusive size="small" value={table.sort} aria-label="Sort events" sx={performanceToggleGroupSx}
                        onChange={(_, value) => { if (value) updateColumns({ ...tableRef.current, sort: value }); }}>
                        <ToggleButton value="date" sx={performanceToggleButtonSx}>By date</ToggleButton>
                        <ToggleButton value="baseline" sx={performanceToggleButtonSx}>By baseline result</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                {firstLoad && (
                    <Typography role="status" variant="caption" color="text.secondary">
                        Replaying today’s holdings through the factor history. The first load for a factor dataset can take a few seconds.
                    </Typography>
                )}
                {libraryQuery.isError && (
                    <Alert severity="warning" action={<Button size="small" onClick={() => { void libraryQuery.refetch(); }}>Retry</Button>}>
                        The stress library couldn’t be loaded, so its events aren’t in the grid. Your events and the worst and best periods still replay.
                    </Alert>
                )}
                {(missingScan && scanUnavailable) && <Alert severity="info">{SCAN_UNAVAILABLE_TEXT}</Alert>}
                {otherReasons.length > 0 && <Alert severity="info">{reasonsText(otherReasons)}</Alert>}
                {gridBody}
                {shownColumns.length > 0 && !gridFailed && contextReady && <StressLegend basis={basis} markets={marketEntries.length > 0} />}
                {shownColumns.length > 0 && !gridFailed && contextReady && (
                    <Typography variant="caption" color="text.secondary">
                        Each cell is a what-if: today’s factor exposures replayed through that event’s factor returns, as if a period like it happened again.
                        {marketEntries.length > 0 && ' Rows tagged Actual are the market’s actual total return over the same dates, from ETF prices with dividends reinvested: history for reference, not a replay. Market returns compound, while a replay adds up each day’s result at today’s position sizes, so over long or volatile periods the two differ even for the same holdings.'}
                        {' '}Click an event above to put it in the table or back on the bench.
                    </Typography>
                )}
            </Paper>

            <Paper component="section" aria-labelledby="compare-explore-history-title" sx={panelSx}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                    <Typography id="compare-explore-history-title" component="h3" sx={{ ...titleSx, mr: 'auto' }}>
                        History · today against the factor history
                    </Typography>
                    {environment && (
                        <Typography variant="caption" color="text.secondary">
                            {[historySpan(environment), `as of ${formatDay(environment.as_of)}`].filter(Boolean).join(' · ')}
                        </Typography>
                    )}
                </Box>
                {historyBody}
            </Paper>

            <Paper component="section" ref={detailRef} aria-labelledby="compare-explore-detail-title" sx={{ ...panelSx, scrollMarginTop: 16 }}>
                <Typography id="compare-explore-detail-title" component="h3" sx={titleSx}>Episode detail</Typography>
                {gridFailed || !contextReady || firstLoad || noReplay
                    ? (
                        <Typography variant="body2" color="text.secondary">
                            {noReplay ? `None of these portfolios can be replayed. ${reasonsText(baselineEntry?.row?.reasons)}` : 'The episode detail appears once the grid has results.'}
                        </Typography>
                    )
                    : (
                        <EpisodeDetail entry={selectedEntry} column={selectedColumn} baselineEntry={baselineEntry}
                            detail={detailQuery} calendar={calendarQuery} refreshing={refreshes > 0} onRefreshGrid={refreshGrid} />
                    )}
            </Paper>

            {editor?.userUid === userUid && (
                <EventEditor key={editor.key} open anchorEl={editor.anchorEl} mode={editor.mode} initial={editor.initial}
                    takenTitles={events.filter((event) => event.id !== editor.eventId).map((event) => event.title)}
                    firstDate={grid?.history?.first_return_date || null} lastDate={grid?.history?.last_return_date || null}
                    tableFull={table.columns.length >= MAX_TABLE_COLUMNS} onClose={closeEditor} onSave={saveEvent} />
            )}

            <Snackbar open={refreshFailed && gridQuery.errorUpdatedAt !== dismissedFailure} autoHideDuration={8000}
                onClose={(_, reason) => { if (reason !== 'clickaway') dismissFailure(); }}>
                <Alert severity="warning" sx={{ width: '100%' }}
                    action={<Button color="inherit" size="small" onClick={() => { dismissFailure(); void gridQuery.refetch(); }}>Retry</Button>}>
                    {isHistoryContextConflict(gridQuery.error)
                        ? 'The portfolios changed while the grid loaded, so it still shows the last results. Retry to replay the current holdings.'
                        : `The stress grid couldn’t be updated, so it still shows the last results. ${gridQuery.error?.message || ''}`.trim()}
                </Alert>
            </Snackbar>
        </Box>
    );
}

CompareExploreTab.propTypes = {
    active: PropTypes.bool,
    userUid: PropTypes.string,
    contextKey: PropTypes.string.isRequired,
    contextReady: PropTypes.bool,
    contextPending: PropTypes.bool,
    listOfWhatIfPortfolios: PropTypes.array,
    savedColumns: PropTypes.shape({
        version: PropTypes.number,
        columns: PropTypes.arrayOf(PropTypes.string),
        sort: PropTypes.oneOf(['date', 'baseline']),
    }),
    onSavedColumnsChange: PropTypes.func,
};
